import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { enrollPractitioner, unenrollPractitioner } from 'api/Prescribe';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

export const useStaffEnrollment = ({ refetch, clearSelection }) => {
  const navigate = useNavigate();
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [unenrollConfirmOpen, setUnenrollConfirmOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const enrollMutation = useCustomMutation({
    mutationFn: (params) => enrollPractitioner(navigate, params),
    invalidateQueryKey: ['enrolled-practitioners'],
    disableAlert: true
  });

  const unenrollMutation = useCustomMutation({
    mutationFn: (params) => unenrollPractitioner(navigate, params),
    invalidateQueryKey: ['enrolled-practitioners'],
    successMessage: 'Provider un-enrolled',
    disableAlert: false
  });

  const handleEnrollRow = useCallback((rowData) => {
    setSelectedProvider(rowData);
    setEnrollModalOpen(true);
  }, []);

  const handleUnenrollRow = useCallback((rowData) => {
    setSelectedProvider(rowData);
    setUnenrollConfirmOpen(true);
  }, []);

  const handleEnrollSelected = useCallback((selectedRows) => {
    if (selectedRows.length >= 1) {
      setSelectedProvider(selectedRows[0]);
      setEnrollModalOpen(true);
    }
  }, []);

  const handleUnenrollSelected = useCallback((selectedRows) => {
    if (selectedRows.length >= 1) {
      setSelectedProvider(selectedRows[0]);
      setUnenrollConfirmOpen(true);
    }
  }, []);

  const closeEnrollModal = useCallback(() => {
    setEnrollModalOpen(false);
    setSelectedProvider(null);
  }, []);

  const closeUnenrollConfirm = useCallback(() => {
    setUnenrollConfirmOpen(false);
    setSelectedProvider(null);
  }, []);

  const handleUnenrollConfirm = useCallback(
    (selectedRows, practiceId) => {
      const providerToUnenroll = selectedProvider || selectedRows[0];
      if (providerToUnenroll) {
        unenrollMutation.mutate(
          { userId: providerToUnenroll.id, practiceId },
          {
            onSuccess: () => {
              refetch();
              clearSelection();
              closeUnenrollConfirm();
            }
          }
        );
      }
    },
    [selectedProvider, unenrollMutation, refetch, clearSelection, closeUnenrollConfirm]
  );

  return {
    enrollModalOpen,
    unenrollConfirmOpen,
    selectedProvider,
    enrollMutation,
    unenrollMutation,
    handleEnrollRow,
    handleUnenrollRow,
    handleEnrollSelected,
    handleUnenrollSelected,
    closeEnrollModal,
    closeUnenrollConfirm,
    handleUnenrollConfirm
  };
};
